/**
 * Universal Carousel Controller
 * Shared carousel logic for Dynamic Gallery sections
 * Features: Keyboard navigation, auto-rotate with pause, accessibility
 */
(function() {
  'use strict';

  /**
   * Initialize carousel for a section
   * @param {Object} config - Configuration object
   * @param {string} config.sectionId - Unique section ID
   * @param {string} config.containerId - Container element ID
   * @param {string} config.slideSelector - CSS selector for slides
   * @param {string} config.contentSelector - CSS selector for content slides
   * @param {string} config.counterSelector - CSS selector for counter spans
   * @param {string} config.navBtnSelector - CSS selector for navigation buttons
   * @param {string} config.contentAreaSelector - CSS selector for content area (for hover pause)
   * @param {boolean} config.autoRotate - Enable auto-rotation
   * @param {number} config.rotateInterval - Auto-rotation interval in ms
   * @param {string} config.namespace - Global namespace for this carousel instance
   */
  function initCarousel(config) {
    const container = document.getElementById(config.containerId);
    
    if (!container) {
      console.warn('Carousel container not found:', config.containerId);
      return;
    }

    const carousel = new CarouselController(container, config);
    
    // Store reference for external access
    container._carouselController = carousel;
    
    // Expose navigation for inline handlers if namespace provided
    if (config.namespace) {
      window[config.namespace] = window[config.namespace] || {};
      window[config.namespace][config.sectionId] = {
        change: function(direction) {
          carousel.navigate(direction);
        },
        select: function(index) {
          carousel.goToSlide(index);
        }
      };
    }

    return carousel;
  }

  /**
   * Main Carousel Controller Class
   */
  function CarouselController(container, config) {
    this.container = container;
    this.config = config;
    this.currentSlide = 0;
    this.autoRotateTimer = null;
    this.isPaused = false;

    // Cache DOM elements
    this.elements = {
      slides: config.slideSelector ? container.querySelectorAll(config.slideSelector) : null,
      contentSlides: config.contentSelector ? container.querySelectorAll(config.contentSelector) : null,
      counterSpans: config.counterSelector ? container.querySelectorAll(config.counterSelector) : null,
      contentArea: config.contentAreaSelector ? container.querySelector(config.contentAreaSelector) : null,
      liveRegion: null
    };

    // Determine total slides from either slides or content slides
    this.totalSlides = this.elements.slides ? this.elements.slides.length : 
                       (this.elements.contentSlides ? this.elements.contentSlides.length : 0);

    if (this.totalSlides === 0) {
      console.warn('No slides found in carousel');
      return;
    }

    this.init();
  }

  CarouselController.prototype = {
    /**
     * Initialize the carousel
     */
    init: function() {
      this.createLiveRegion();
      this.setupEventListeners();
      this.setupKeyboardNavigation();
      this.updateCarousel();
      this.startAutoRotate();
    },

    /**
     * Create ARIA live region for screen reader announcements
     */
    createLiveRegion: function() {
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0;';
      this.container.appendChild(liveRegion);
      this.elements.liveRegion = liveRegion;
    },

    /**
     * Setup event listeners
     */
    setupEventListeners: function() {
      const self = this;

      // Navigation buttons (event delegation)
      this.container.addEventListener('click', function(e) {
        const button = e.target.closest(self.config.navBtnSelector || '.carousel-nav-btn');
        if (!button) return;

        if (button.classList.contains('prev')) {
          self.navigate(-1);
        } else if (button.classList.contains('next')) {
          self.navigate(1);
        }
      });

      // Pause on hover/focus
      if (this.elements.contentArea) {
        this.elements.contentArea.addEventListener('mouseenter', function() {
          self.pauseAutoRotate();
        });

        this.elements.contentArea.addEventListener('mouseleave', function() {
          self.resumeAutoRotate();
        });

        this.elements.contentArea.addEventListener('focusin', function() {
          self.pauseAutoRotate();
        });

        this.elements.contentArea.addEventListener('focusout', function() {
          self.resumeAutoRotate();
        });
      }
    },

    /**
     * Setup keyboard navigation
     */
    setupKeyboardNavigation: function() {
      const self = this;

      document.addEventListener('keydown', function(e) {
        if (!self.isInViewportOrFocused()) return;

        switch(e.key) {
          case 'ArrowLeft':
          case 'Left':
            e.preventDefault();
            self.navigate(-1);
            break;
          case 'ArrowRight':
          case 'Right':
            e.preventDefault();
            self.navigate(1);
            break;
          case 'Home':
            e.preventDefault();
            self.goToSlide(0);
            break;
          case 'End':
            e.preventDefault();
            self.goToSlide(self.totalSlides - 1);
            break;
        }
      });
    },

    /**
     * Check if carousel is in viewport or has focus
     */
    isInViewportOrFocused: function() {
      if (this.container.contains(document.activeElement)) {
        return true;
      }

      const rect = this.container.getBoundingClientRect();
      return (
        rect.top < window.innerHeight &&
        rect.bottom > 0
      );
    },

    /**
     * Navigate to next/previous slide
     */
    navigate: function(direction) {
      this.currentSlide += direction;
      this.updateCarousel();
      this.resetAutoRotate();
    },

    /**
     * Go to specific slide
     */
    goToSlide: function(index) {
      this.currentSlide = index;
      this.updateCarousel();
      this.resetAutoRotate();
    },

    /**
     * Update the carousel display
     */
    updateCarousel: function() {
      try {
        // Handle wrap around
        if (this.currentSlide >= this.totalSlides) {
          this.currentSlide = 0;
        }
        if (this.currentSlide < 0) {
          this.currentSlide = this.totalSlides - 1;
        }

        // Update slides (if they exist)
        if (this.elements.slides) {
          for (let i = 0; i < this.elements.slides.length; i++) {
            if (i === this.currentSlide) {
              this.elements.slides[i].classList.add('active');
            } else {
              this.elements.slides[i].classList.remove('active');
            }
          }
        }

        // Update content slides if they exist
        if (this.elements.contentSlides) {
          for (let j = 0; j < this.elements.contentSlides.length; j++) {
            const isActive = j === this.currentSlide;
            if (isActive) {
              this.elements.contentSlides[j].classList.add('active');
              this.elements.contentSlides[j].setAttribute('aria-hidden', 'false');
            } else {
              this.elements.contentSlides[j].classList.remove('active');
              this.elements.contentSlides[j].setAttribute('aria-hidden', 'true');
            }
          }
        }

        // Update counter
        if (this.elements.counterSpans) {
          for (let k = 0; k < this.elements.counterSpans.length; k++) {
            this.elements.counterSpans[k].textContent = this.currentSlide + 1;
          }
        }

        // Announce to screen readers
        this.announceSlideChange();
      } catch (error) {
        console.error('Error updating carousel:', error);
      }
    },

    /**
     * Announce slide change to screen readers
     */
    announceSlideChange: function() {
      if (!this.elements.liveRegion) return;

      let title = '';
      const currentSlide = this.elements.slides[this.currentSlide];
      
      if (currentSlide) {
        // Try to find title in current slide or content slide
        const titleSelectors = ['.gallery-showcase-title', '.dynamic-gallery-info-name', 'h2', 'h3', 'h4'];
        for (let selector of titleSelectors) {
          const titleEl = currentSlide.querySelector(selector);
          if (titleEl && titleEl.textContent.trim()) {
            title = titleEl.textContent.trim();
            break;
          }
        }
        
        // If not found in slide, check content slides
        if (!title && this.elements.contentSlides && this.elements.contentSlides[this.currentSlide]) {
          for (let selector of titleSelectors) {
            const titleEl = this.elements.contentSlides[this.currentSlide].querySelector(selector);
            if (titleEl && titleEl.textContent.trim()) {
              title = titleEl.textContent.trim();
              break;
            }
          }
        }
      }

      this.elements.liveRegion.textContent =
        'Slide ' + (this.currentSlide + 1) + ' of ' + this.totalSlides +
        (title ? ': ' + title : '');
    },

    /**
     * Start auto-rotate timer
     */
    startAutoRotate: function() {
      if (!this.config.autoRotate) return;

      const self = this;
      this.autoRotateTimer = setInterval(function() {
        if (!self.isPaused) {
          self.currentSlide++;
          self.updateCarousel();
        }
      }, this.config.rotateInterval || 5000);
    },

    /**
     * Pause auto-rotate
     */
    pauseAutoRotate: function() {
      this.isPaused = true;
    },

    /**
     * Resume auto-rotate
     */
    resumeAutoRotate: function() {
      this.isPaused = false;
    },

    /**
     * Reset auto-rotate timer
     */
    resetAutoRotate: function() {
      if (!this.config.autoRotate || !this.autoRotateTimer) return;
      
      clearInterval(this.autoRotateTimer);
      this.startAutoRotate();
    }
  };

  // Expose to global scope
  window.CarouselController = {
    init: initCarousel
  };

  // Auto-initialize on DOM ready
  function autoInit() {
    // Look for carousel containers with data attributes
    const containers = document.querySelectorAll('[data-carousel-config]');
    containers.forEach(function(container) {
      try {
        const config = JSON.parse(container.getAttribute('data-carousel-config'));
        initCarousel(config);
      } catch (error) {
        console.error('Error auto-initializing carousel:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
})();

